import { classnames } from '@app/utils/index.js';
import type { CheckoutFormModel } from '@features/purchasing/checkout/schema';
import Image from '@store/components/Image';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import type { IPaymentMethod, IShippingMethod } from '../types';

type IProps = {
  isAllowed: boolean;
  selectedMethodId?: string;
  onSelect?: () => void;
  onClick?: () => void;
  disabled?: boolean;
} & (IShippingMethod | IPaymentMethod);

export default function Method({
  isAllowed,
  selectedMethodId,
  id,
  price,
  name,
  imageUrl,
  disabled,
  ...props
}: IProps) {
  const { register, setValue } = useFormContext<CheckoutFormModel>();
  const isShipping = 'citizenPayments' in props || 'companyPayments' in props;

  const isDisabled = useMemo(() => disabled || !isAllowed, [disabled, isAllowed]);

  if (!isAllowed) {
    return <></>;
  }

  return (
    <div className="form-field">
      <label
        className={classnames(
          'flex items-center gap-x-2 px-4 py-2 border rounded-none laptop:text-base',
          {
            'cursor-pointer': !isDisabled,
            'shadow-md': id === selectedMethodId,
            'shadow-primary': id === selectedMethodId,
            'bg-gray-100': isDisabled,
            'hover:shadow-md': !isDisabled,
            'hover:shadow-primary': isAllowed && id === selectedMethodId,
          }
        )}>
        <input
          {...register(isShipping ? 'shippingMethodId' : 'paymentMethodId')}
          type="checkbox"
          className="hidden"
          disabled={isDisabled}
          onClick={props.onClick}
          onChange={() => {
            setValue(isShipping ? 'shippingMethodId' : 'paymentMethodId', id);
            if (isShipping) {
              setValue('paymentMethodId', '');
            }
            if (props.onSelect) props.onSelect();
          }}
        />

        <div className="flex flex-1 items-center gap-x-4">
          <span className="icon h-[32px] w-[32px] inline-flex items-center justify-center">
            {imageUrl && (
              <Image
                src={imageUrl}
                alt={name}
                className="max-w-full max-h-full object-scale-down"
                width={32}
                height={32}
              />
            )}
          </span>
          <span className="flex-1">{name}</span>
          <div className="flex flex-col mr-auto">
            {price?.formatted ? (
              <span className="">+{price.formatted}</span>
            ) : (
              <span>naceníme</span>
            )}
          </div>
        </div>
      </label>
    </div>
  );
}
